import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { CareNavigationRosterResponse } from "@t3tools/contracts";
import { Schema } from "effect";
import type {
  EnrollmentCohortCurvesResponse,
  EnrollmentMonthlyConversionResponse,
  EnrollmentCohortStatusOptions,
  EnrollmentCohortStatusResponse,
} from "@t3tools/contracts";
import { isEnrollmentCohortCurvesResponse } from "./enrollmentCohortCurves";
import { isCohortStatusOptions, isCohortStatusResponse } from "./enrollmentCohortStatus";
import { isEnrollmentMonthlyConversionResponse } from "./enrollmentMonthlyConversion";
import type {
  CareNavigationDefinitionRequest,
  CareNavigationDefinitionResponse,
  CareNavigationMonthlyCompletionResponse,
} from "./careNavigationCriteria";

import { getOperatorsHttpOrigin } from "../../operators";
import {
  readOperatorsAuthErrorMessage,
  throwOperatorsAuthExpired,
} from "../../operatorsAuthSession";
import { OperatorsForbiddenError, operatorsQueryKeys } from "../../lib/operatorsReactQuery";
import {
  type AttritionRecordsParams,
  type AttritionRecordsResponse,
  type AttritionSummaryResponse,
  type AttritionTimeframe,
  isAttritionRecordsResponse,
  isAttritionSummaryResponse,
} from "./attritionTypes";
import {
  type EngVelocitySnapshotResponse,
  isEngVelocitySnapshotResponse,
} from "./engVelocityTypes";
import {
  type AiEngineeringSnapshotResponse,
  normalizeAiEngineeringSnapshot,
} from "./aiEngineeringTypes";
import {
  type CareNavigationEncounterReviewsParams,
  type CareNavigationEncounterReviewsResponse,
  isCareNavigationEncounterReviewsResponse,
} from "./cnCallReviewTypes";
import {
  type ClinicHealthResponse,
  type DataCoverageResponse,
  type OnboardingHealthResponse,
  type AccountManagementSnapshotResponse,
  isClinicHealthResponse,
  isDataCoverageResponse,
  isOnboardingHealthResponse,
  isAccountManagementSnapshotResponse,
} from "./accountManagementTypes";
import { retellCallsSummaryQueryOptions } from "./retellCallsReactQuery";
import { etYesterdayRange } from "./retellCallsTheme";
import {
  type EnrollmentBookingSourcesResponse,
  type EnrollmentEodCsvResponse,
  type EnrollmentEodResponse,
  type EnrollmentEsConversionResponse,
  type EnrollmentEsOpsCallsResponse,
  type EnrollmentEsOpsResponse,
  type EnrollmentNeuroAppointmentsResponse,
  type EnrollmentReferralsResponse,
  type EnrollmentSnapshotResponse,
  type EnrollmentSnapshotsResponse,
  type EnrollmentTrendResponse,
  isEnrollmentBookingSourcesResponse,
  isEnrollmentEodCsvResponse,
  isEnrollmentEodResponse,
  isEnrollmentEsConversionResponse,
  isEnrollmentEsOpsCallsResponse,
  isEnrollmentEsOpsResponse,
  isEnrollmentNeuroAppointmentsResponse,
  isEnrollmentReferralsResponse,
  isEnrollmentSnapshotResponse,
  isEnrollmentSnapshotsResponse,
  isEnrollmentTrendResponse,
} from "./enrollmentTypes";
import {
  type CareNavigationCalendarStatusRequest,
  type CareNavigationCalendarStatusResponse,
  type CareNavigationDrilldownRequest,
  type CareNavigationDrilldownResponse,
  type CareNavigationSnapshotResponse,
  type CareNavigationTimeframe,
  isCareNavigationCalendarStatusResponse,
  isCareNavigationDrilldownResponse,
  isCareNavigationSnapshotResponse,
} from "./careNavigationTypes";
import {
  type ProductPrioritiesResponse,
  isProductPrioritiesResponse,
} from "./productPrioritiesTypes";
import { calKillerSnapshotQueryOptions } from "./calKillerReactQuery";

export type EnrollmentTrendParams = {
  end?: string;
  start?: string;
  window?: "monthly" | "weekly";
};

export type EnrollmentEodParams = {
  date?: string;
  includeDetails?: boolean;
  includeReferrals?: boolean;
  section?: "calls" | "conversion" | "cumulative";
};

export const dashboardsQueryKeys = {
  all: [...operatorsQueryKeys.all, "dashboards"] as const,
  enrollmentCohortCurves: () =>
    [...dashboardsQueryKeys.all, "enrollment", "cohort-curves"] as const,
  engVelocity: () => [...dashboardsQueryKeys.all, "eng-velocity"] as const,
  aiEngineering: () => [...dashboardsQueryKeys.all, "eng-velocity", "ai-engineering"] as const,
  productPriorities: () => [...dashboardsQueryKeys.all, "product-priorities"] as const,
  calKiller: (filters: import("./calKillerTypes").CalKillerFilters = {}) =>
    [...dashboardsQueryKeys.all, "cal-killer", filters] as const,
  attritionSummary: (timeframe: AttritionTimeframe) =>
    [...dashboardsQueryKeys.all, "attrition", "summary", timeframe] as const,
  attritionRecords: (params: AttritionRecordsParams) =>
    [...dashboardsQueryKeys.all, "attrition", "records", params] as const,
  cnCallReview: (params: CareNavigationEncounterReviewsParams) =>
    [...dashboardsQueryKeys.all, "cn-call-review", "call-reviews", params] as const,
  accountManagementClinicHealth: (asOf?: string) =>
    [...dashboardsQueryKeys.all, "account-management", "clinic-health", asOf ?? null] as const,
  accountManagementDataCoverage: (asOf?: string) =>
    [...dashboardsQueryKeys.all, "account-management", "data-coverage", asOf ?? null] as const,
  accountManagementOnboardingHealth: (asOf?: string) =>
    [...dashboardsQueryKeys.all, "account-management", "onboarding-health", asOf ?? null] as const,
  accountManagementSnapshot: (asOf?: string) =>
    [...dashboardsQueryKeys.all, "account-management", "snapshot", asOf ?? null] as const,
  enrollmentSnapshot: (date?: string) =>
    [...dashboardsQueryKeys.all, "enrollment", "snapshot", date ?? null] as const,
  enrollmentSnapshots: (dates: string[]) =>
    [...dashboardsQueryKeys.all, "enrollment", "snapshots", dates] as const,
  enrollmentEod: (params: EnrollmentEodParams) =>
    [...dashboardsQueryKeys.all, "enrollment", "eod", params] as const,
  enrollmentEodCsv: (date?: string) =>
    [...dashboardsQueryKeys.all, "enrollment", "eod-csv", date ?? null] as const,
  enrollmentReferrals: (date?: string) =>
    [...dashboardsQueryKeys.all, "enrollment", "referrals", date ?? null] as const,
  enrollmentBookingSources: (date?: string) =>
    [...dashboardsQueryKeys.all, "enrollment", "booking-sources", date ?? null] as const,
  enrollmentNeuroAppointments: (date?: string) =>
    [...dashboardsQueryKeys.all, "enrollment", "neuro-appointments", date ?? null] as const,
  enrollmentEsOps: (date?: string) =>
    [...dashboardsQueryKeys.all, "enrollment", "es-ops", date ?? null] as const,
  enrollmentEsOpsCalls: (date?: string) =>
    [...dashboardsQueryKeys.all, "enrollment", "es-ops-calls", date ?? null] as const,
  enrollmentEsConversion: (date?: string) =>
    [...dashboardsQueryKeys.all, "enrollment", "es-conversion", date ?? null] as const,
  enrollmentTrend: (params: EnrollmentTrendParams) =>
    [...dashboardsQueryKeys.all, "enrollment", "trend", params] as const,
  careNavigationSnapshot: (params: CareNavigationTimeframe) =>
    [...dashboardsQueryKeys.all, "care-navigation", "snapshot", params] as const,
  careNavigationDrilldown: (params: CareNavigationDrilldownRequest) =>
    [...dashboardsQueryKeys.all, "care-navigation", "drilldown", params] as const,
  careNavigationCalendarStatus: (params: CareNavigationCalendarStatusRequest) =>
    [...dashboardsQueryKeys.all, "care-navigation", "calendar-status", params] as const,
};

export function enrollmentCohortCurvesQueryOptions() {
  return queryOptions({
    queryKey: dashboardsQueryKeys.enrollmentCohortCurves(),
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<EnrollmentCohortCurvesResponse> => {
      const body = await fetchEnrollmentJson(
        "/api/v1/dashboards/enrollment/cohort-curves",
        "Failed to load enrollment cohort curves",
      );
      if (!isEnrollmentCohortCurvesResponse(body)) {
        throw new Error("Enrollment cohort curves response was malformed");
      }
      return body;
    },
  });
}

export function enrollmentCohortStatusOptionsQueryOptions() {
  return queryOptions({
    queryKey: [...dashboardsQueryKeys.all, "enrollment", "cohort-status-options"] as const,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<EnrollmentCohortStatusOptions> => {
      const body = await fetchEnrollmentJson(
        "/api/v1/dashboards/enrollment/cohort-status-options",
        "Failed to load cohort filters",
      );
      if (!isCohortStatusOptions(body)) throw new Error("Cohort filters response was malformed");
      return body;
    },
  });
}

export function enrollmentCohortStatusQueryOptions(month: string, clinicIds: string[]) {
  const ids = [...new Set(clinicIds)].sort();
  return queryOptions({
    queryKey: [...dashboardsQueryKeys.all, "enrollment", "cohort-status", month, ids] as const,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<EnrollmentCohortStatusResponse> => {
      const query = new URLSearchParams({ month, clinics: ids.join(",") });
      const body = await fetchEnrollmentJson(
        `/api/v1/dashboards/enrollment/cohort-status?${query}`,
        "Failed to load cohort statuses",
      );
      if (
        !isCohortStatusResponse(body) ||
        body.month !== month ||
        body.clinicIds.join(",") !== ids.join(",")
      )
        throw new Error("Cohort statuses response was malformed");
      return body;
    },
  });
}

export function enrollmentMonthlyConversionQueryOptions() {
  return queryOptions({
    queryKey: [...dashboardsQueryKeys.all, "enrollment", "monthly-cohort-conversion"] as const,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<EnrollmentMonthlyConversionResponse> => {
      const body = await fetchEnrollmentJson(
        "/api/v1/dashboards/enrollment/monthly-cohort-conversion",
        "Failed to load monthly cohort conversion",
      );
      if (!isEnrollmentMonthlyConversionResponse(body)) {
        throw new Error("Monthly cohort conversion response was malformed");
      }
      return body;
    },
  });
}

function throwIfUnauthorized(response: Response): void {
  if (response.status === 401) {
    throwOperatorsAuthExpired();
  }
}

async function throwIfForbidden(response: Response, fallbackMessage: string): Promise<void> {
  if (response.status !== 403) {
    return;
  }

  const message = await readOperatorsAuthErrorMessage(response).catch(() => fallbackMessage);
  throw new OperatorsForbiddenError(message || fallbackMessage);
}

export async function fetchEngVelocitySnapshot(): Promise<EngVelocitySnapshotResponse> {
  const response = await fetch(`${getOperatorsHttpOrigin()}/api/v1/dashboards/eng-velocity`, {
    credentials: "include",
  });
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have access to Dashboards.");
  if (!response.ok) {
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(
        () => "Failed to load Eng Velocity dashboard",
      ),
    );
  }
  const body: unknown = await response.json();
  if (!isEngVelocitySnapshotResponse(body)) {
    throw new Error("Eng Velocity response was malformed");
  }
  return body;
}

export function engVelocityQueryOptions() {
  return queryOptions({
    queryKey: dashboardsQueryKeys.engVelocity(),
    queryFn: fetchEngVelocitySnapshot,
    staleTime: 5 * 60_000,
    refetchInterval: 15 * 60_000,
    refetchOnWindowFocus: true,
  });
}

export async function fetchAiEngineeringSnapshot(): Promise<AiEngineeringSnapshotResponse> {
  const response = await fetch(
    `${getOperatorsHttpOrigin()}/api/v1/dashboards/eng-velocity/ai-engineering`,
    { credentials: "include" },
  );
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have access to Dashboards.");
  if (!response.ok) {
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(
        () => "Failed to load AI Engineering dashboard",
      ),
    );
  }
  const body: unknown = await response.json();
  const snapshot = normalizeAiEngineeringSnapshot(body);
  if (!snapshot) {
    throw new Error("AI Engineering response was malformed");
  }
  return snapshot;
}

export function aiEngineeringQueryOptions() {
  return queryOptions({
    queryKey: dashboardsQueryKeys.aiEngineering(),
    queryFn: fetchAiEngineeringSnapshot,
  });
}

export async function fetchProductPriorities(): Promise<ProductPrioritiesResponse> {
  const response = await fetch(`${getOperatorsHttpOrigin()}/api/v1/dashboards/product-priorities`, {
    credentials: "include",
  });
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have access to Dashboards.");
  if (!response.ok) {
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(
        () => "Failed to load Product Priorities dashboard",
      ),
    );
  }
  const body: unknown = await response.json();
  if (!isProductPrioritiesResponse(body)) {
    throw new Error("Product Priorities response was malformed");
  }
  return body;
}

export function productPrioritiesQueryOptions() {
  return queryOptions({
    queryKey: dashboardsQueryKeys.productPriorities(),
    queryFn: fetchProductPriorities,
  });
}

export async function fetchAttritionSummary(
  timeframe: AttritionTimeframe,
): Promise<AttritionSummaryResponse> {
  const params = new URLSearchParams({
    dateStart: timeframe.dateStart,
    dateEnd: timeframe.dateEnd,
    timezone: timeframe.timezone,
  });
  const response = await fetch(
    `${getOperatorsHttpOrigin()}/api/v1/dashboards/attrition/summary?${params}`,
    { credentials: "include" },
  );
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have access to Dashboards.");
  if (!response.ok) {
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(() => "Failed to load Attrition summary"),
    );
  }
  const body: unknown = await response.json();
  if (!isAttritionSummaryResponse(body)) {
    throw new Error("Attrition summary response was malformed");
  }
  return body;
}

export function attritionSummaryQueryOptions(timeframe: AttritionTimeframe) {
  return queryOptions({
    queryKey: dashboardsQueryKeys.attritionSummary(timeframe),
    queryFn: () => fetchAttritionSummary(timeframe),
  });
}

export async function fetchAttritionRecords(
  params: AttritionRecordsParams,
): Promise<AttritionRecordsResponse> {
  const search = new URLSearchParams({
    dateStart: params.dateStart,
    dateEnd: params.dateEnd,
    timezone: params.timezone,
    outcome: params.outcome,
    limit: String(params.limit ?? 50),
    offset: String(params.offset ?? 0),
  });
  if (params.reason) {
    search.set("reason", params.reason);
  }

  const response = await fetch(
    `${getOperatorsHttpOrigin()}/api/v1/dashboards/attrition/records?${search}`,
    { credentials: "include" },
  );
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have access to Dashboards.");
  if (!response.ok) {
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(() => "Failed to load Attrition records"),
    );
  }
  const body: unknown = await response.json();
  if (!isAttritionRecordsResponse(body)) {
    throw new Error("Attrition records response was malformed");
  }
  return body;
}

export function attritionRecordsQueryOptions(params: AttritionRecordsParams) {
  return queryOptions({
    queryKey: dashboardsQueryKeys.attritionRecords(params),
    queryFn: () => fetchAttritionRecords(params),
  });
}

export async function fetchCnCallReviews(
  params: CareNavigationEncounterReviewsParams,
): Promise<CareNavigationEncounterReviewsResponse> {
  const search = new URLSearchParams({
    start: params.start,
    end: params.end,
  });
  if (params.encounterTypes?.length) {
    search.set("encounterTypes", params.encounterTypes.join(","));
  }
  if (params.minLengthMinutes != null) {
    search.set("minLengthMinutes", String(params.minLengthMinutes));
  }
  if (params.limit != null) {
    search.set("limit", String(params.limit));
  }
  const response = await fetch(
    `${getOperatorsHttpOrigin()}/api/v1/dashboards/cn-call-review/call-reviews?${search}`,
    { credentials: "include" },
  );
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have access to Dashboards.");
  if (!response.ok) {
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(
        () => "Failed to load CN Call Review encounter reviews",
      ),
    );
  }
  const body: unknown = await response.json();
  if (!isCareNavigationEncounterReviewsResponse(body)) {
    throw new Error("CN Call Review encounter reviews response was malformed");
  }
  return body;
}

export function cnCallReviewQueryOptions(params: CareNavigationEncounterReviewsParams) {
  return queryOptions({
    queryKey: dashboardsQueryKeys.cnCallReview(params),
    queryFn: () => fetchCnCallReviews(params),
    enabled: Boolean(params.start && params.end && params.start <= params.end),
  });
}

export async function fetchAccountManagementClinicHealth(
  asOf?: string,
): Promise<ClinicHealthResponse> {
  const search = new URLSearchParams();
  if (asOf) search.set("asOf", asOf);
  const suffix = search.size > 0 ? `?${search}` : "";
  const response = await fetch(
    `${getOperatorsHttpOrigin()}/api/v1/dashboards/account-management/clinic-health${suffix}`,
    { credentials: "include" },
  );
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have access to Dashboards.");
  if (!response.ok) {
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(
        () => "Failed to load Account Management clinic health",
      ),
    );
  }
  const body: unknown = await response.json();
  if (!isClinicHealthResponse(body)) {
    throw new Error("Account Management clinic health response was malformed");
  }
  return body;
}

/** These payloads are as-of-date snapshots, so intraday refetching buys nothing. */
const ACCOUNT_MANAGEMENT_STALE_TIME_MS = 15 * 60_000;

export function accountManagementClinicHealthQueryOptions(asOf?: string) {
  return queryOptions({
    queryKey: dashboardsQueryKeys.accountManagementClinicHealth(asOf),
    queryFn: () => fetchAccountManagementClinicHealth(asOf),
    staleTime: ACCOUNT_MANAGEMENT_STALE_TIME_MS,
  });
}

export async function fetchAccountManagementDataCoverage(
  asOf?: string,
): Promise<DataCoverageResponse> {
  const search = new URLSearchParams();
  if (asOf) search.set("asOf", asOf);
  const suffix = search.size > 0 ? `?${search}` : "";
  const response = await fetch(
    `${getOperatorsHttpOrigin()}/api/v1/dashboards/account-management/data-coverage${suffix}`,
    { credentials: "include" },
  );
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have access to Dashboards.");
  if (!response.ok) {
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(
        () => "Failed to load Account Management data coverage",
      ),
    );
  }
  const body: unknown = await response.json();
  if (!isDataCoverageResponse(body)) {
    throw new Error("Account Management data coverage response was malformed");
  }
  return body;
}

export function accountManagementDataCoverageQueryOptions(asOf?: string) {
  return queryOptions({
    queryKey: dashboardsQueryKeys.accountManagementDataCoverage(asOf),
    queryFn: () => fetchAccountManagementDataCoverage(asOf),
    staleTime: ACCOUNT_MANAGEMENT_STALE_TIME_MS,
  });
}

export async function fetchAccountManagementOnboardingHealth(
  asOf?: string,
): Promise<OnboardingHealthResponse> {
  const search = new URLSearchParams();
  if (asOf) search.set("asOf", asOf);
  const suffix = search.size > 0 ? `?${search}` : "";
  const response = await fetch(
    `${getOperatorsHttpOrigin()}/api/v1/dashboards/account-management/onboarding-health${suffix}`,
    { credentials: "include" },
  );
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have access to Dashboards.");
  if (!response.ok) {
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(
        () => "Failed to load Account Management onboarding health",
      ),
    );
  }
  const body: unknown = await response.json();
  if (!isOnboardingHealthResponse(body)) {
    throw new Error("Account Management onboarding health response was malformed");
  }
  return body;
}

export function accountManagementOnboardingHealthQueryOptions(asOf?: string) {
  return queryOptions({
    queryKey: dashboardsQueryKeys.accountManagementOnboardingHealth(asOf),
    queryFn: () => fetchAccountManagementOnboardingHealth(asOf),
    staleTime: ACCOUNT_MANAGEMENT_STALE_TIME_MS,
  });
}

/**
 * Rebuilds the snapshot from the three per-tab endpoints it replaced.
 *
 * Netlify publishes from `main` on merge while the control plane deploys by
 * hand, so the browser is newer than the API for as long as that gap lasts.
 * Without this the whole page fails instead of one tab, because all three tabs
 * now ride a single request. Still parallel, so the fallback beats the chained
 * fetches this endpoint replaced.
 */
async function fetchAccountManagementSnapshotFromParts(
  asOf?: string,
): Promise<AccountManagementSnapshotResponse> {
  const [clinicHealth, dataCoverage, onboardingHealth] = await Promise.all([
    fetchAccountManagementClinicHealth(asOf),
    fetchAccountManagementDataCoverage(asOf),
    fetchAccountManagementOnboardingHealth(asOf),
  ]);
  return { clinicHealth, dataCoverage, onboardingHealth };
}

export async function fetchAccountManagementSnapshot(
  asOf?: string,
): Promise<AccountManagementSnapshotResponse> {
  const search = new URLSearchParams();
  if (asOf) search.set("asOf", asOf);
  const suffix = search.size > 0 ? `?${search}` : "";
  const response = await fetch(
    `${getOperatorsHttpOrigin()}/api/v1/dashboards/account-management/snapshot${suffix}`,
    { credentials: "include" },
  );
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have access to Dashboards.");
  if (response.status === 404) {
    return fetchAccountManagementSnapshotFromParts(asOf);
  }
  if (!response.ok) {
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(
        () => "Failed to load Account Management snapshot",
      ),
    );
  }
  const body: unknown = await response.json();
  if (!isAccountManagementSnapshotResponse(body)) {
    throw new Error("Account Management snapshot response was malformed");
  }
  return body;
}

export function accountManagementSnapshotQueryOptions(asOf?: string) {
  return queryOptions({
    queryKey: dashboardsQueryKeys.accountManagementSnapshot(asOf),
    queryFn: () => fetchAccountManagementSnapshot(asOf),
    staleTime: ACCOUNT_MANAGEMENT_STALE_TIME_MS,
  });
}

export function prefetchRetellCallsDashboard(queryClient: QueryClient): Promise<void> {
  return queryClient.prefetchQuery(retellCallsSummaryQueryOptions(etYesterdayRange()));
}

export function prefetchAccountManagementDashboard(
  queryClient: QueryClient,
  asOf?: string,
): Promise<void> {
  return queryClient.prefetchQuery(accountManagementSnapshotQueryOptions(asOf));
}

/**
 * Warms a dashboard from the nav so the page has data in cache before it mounts.
 *
 * Only safe to call from inside the authenticated dashboards shell: a 401 here
 * routes through throwOperatorsAuthExpired, which signs the operator out. That
 * rules out a router loader, which would run before the session check resolves.
 */
export function prefetchDashboardBySlug(
  queryClient: QueryClient,
  slug: string,
): Promise<void> | undefined {
  if (slug === "retell-calls") {
    return prefetchRetellCallsDashboard(queryClient);
  }
  if (slug === "account-management") {
    return prefetchAccountManagementDashboard(queryClient);
  }
  if (slug === "cal-killer") {
    return queryClient.prefetchQuery(calKillerSnapshotQueryOptions());
  }
  return undefined;
}

export async function fetchEnrollmentJson(path: string, fallbackMessage: string): Promise<unknown> {
  const response = await fetch(`${getOperatorsHttpOrigin()}${path}`, {
    credentials: "include",
  });
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have access to Dashboards.");
  if (!response.ok) {
    throw new Error(await readOperatorsAuthErrorMessage(response).catch(() => fallbackMessage));
  }
  const body: unknown = await response.json();
  if (typeof body === "object" && body !== null && "source" in body && body.source === "mock") {
    throw new Error("Live enrollment data is unavailable. Please try again.");
  }
  return body;
}

export async function fetchEnrollmentSnapshot(date?: string): Promise<EnrollmentSnapshotResponse> {
  const search = new URLSearchParams();
  if (date) search.set("date", date);
  const suffix = search.size > 0 ? `?${search}` : "";
  const body = await fetchEnrollmentJson(
    `/api/v1/dashboards/enrollment/snapshot${suffix}`,
    "Failed to load Enrollment snapshot",
  );
  if (!isEnrollmentSnapshotResponse(body)) {
    throw new Error("Enrollment snapshot response was malformed");
  }
  return body;
}

export function enrollmentSnapshotQueryOptions(date?: string) {
  return queryOptions({
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: 1,
    queryKey: dashboardsQueryKeys.enrollmentSnapshot(date),
    queryFn: () => fetchEnrollmentSnapshot(date),
  });
}

export async function fetchEnrollmentSnapshots(
  dates: string[],
): Promise<EnrollmentSnapshotsResponse> {
  const search = new URLSearchParams({ dates: dates.join(",") });
  const body = await fetchEnrollmentJson(
    `/api/v1/dashboards/enrollment/snapshot?${search}`,
    "Failed to load Enrollment snapshots",
  );
  if (!isEnrollmentSnapshotsResponse(body)) {
    throw new Error("Enrollment snapshots response was malformed");
  }
  return body;
}

export function enrollmentSnapshotsQueryOptions(dates: string[]) {
  return queryOptions({
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: 1,
    queryKey: dashboardsQueryKeys.enrollmentSnapshots(dates),
    queryFn: () => fetchEnrollmentSnapshots(dates),
    enabled: dates.length > 0,
  });
}

export async function fetchEnrollmentEod(
  params: EnrollmentEodParams = {},
): Promise<EnrollmentEodResponse> {
  const search = new URLSearchParams();
  if (params.date) search.set("date", params.date);
  if (params.includeDetails === false) search.set("details", "0");
  if (params.includeReferrals === false) search.set("referrals", "0");
  if (params.section) search.set("section", params.section);
  const suffix = search.size > 0 ? `?${search}` : "";
  const body = await fetchEnrollmentJson(
    `/api/v1/dashboards/enrollment/eod${suffix}`,
    "Failed to load Enrollment EOD",
  );
  if (!isEnrollmentEodResponse(body)) {
    throw new Error("Enrollment EOD response was malformed");
  }
  return body;
}

export function enrollmentEodQueryOptions(params: EnrollmentEodParams = {}) {
  return queryOptions({
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: 1,
    queryKey: dashboardsQueryKeys.enrollmentEod(params),
    queryFn: () => fetchEnrollmentEod(params),
  });
}

export async function fetchEnrollmentEodCsv(date?: string): Promise<EnrollmentEodCsvResponse> {
  const search = new URLSearchParams();
  if (date) search.set("date", date);
  const suffix = search.size > 0 ? `?${search}` : "";
  const body = await fetchEnrollmentJson(
    `/api/v1/dashboards/enrollment/eod-csv${suffix}`,
    "Failed to load Enrollment EOD CSV",
  );
  if (!isEnrollmentEodCsvResponse(body)) {
    throw new Error("Enrollment EOD CSV response was malformed");
  }
  return body;
}

export async function fetchEnrollmentReferrals(
  date?: string,
): Promise<EnrollmentReferralsResponse> {
  const search = new URLSearchParams();
  if (date) search.set("date", date);
  const suffix = search.size > 0 ? `?${search}` : "";
  const body = await fetchEnrollmentJson(
    `/api/v1/dashboards/enrollment/referrals${suffix}`,
    "Failed to load Enrollment referrals",
  );
  if (!isEnrollmentReferralsResponse(body)) {
    throw new Error("Enrollment referrals response was malformed");
  }
  return body;
}

export function enrollmentReferralsQueryOptions(date?: string) {
  return queryOptions({
    queryKey: dashboardsQueryKeys.enrollmentReferrals(date),
    queryFn: () => fetchEnrollmentReferrals(date),
  });
}

export async function fetchEnrollmentBookingSources(
  date?: string,
): Promise<EnrollmentBookingSourcesResponse> {
  const search = new URLSearchParams();
  if (date) search.set("date", date);
  const suffix = search.size > 0 ? `?${search}` : "";
  const body = await fetchEnrollmentJson(
    `/api/v1/dashboards/enrollment/booking-sources${suffix}`,
    "Failed to load Enrollment booking sources",
  );
  if (!isEnrollmentBookingSourcesResponse(body)) {
    throw new Error("Enrollment booking sources response was malformed");
  }
  return body;
}

export function enrollmentBookingSourcesQueryOptions(date?: string) {
  return queryOptions({
    queryKey: dashboardsQueryKeys.enrollmentBookingSources(date),
    queryFn: () => fetchEnrollmentBookingSources(date),
  });
}

export async function fetchEnrollmentNeuroAppointments(
  date?: string,
): Promise<EnrollmentNeuroAppointmentsResponse> {
  const search = new URLSearchParams();
  if (date) search.set("date", date);
  const suffix = search.size > 0 ? `?${search}` : "";
  const body = await fetchEnrollmentJson(
    `/api/v1/dashboards/enrollment/neuro-appointments${suffix}`,
    "Failed to load Enrollment neuro appointments",
  );
  if (!isEnrollmentNeuroAppointmentsResponse(body)) {
    throw new Error("Enrollment neuro appointments response was malformed");
  }
  return body;
}

export function enrollmentNeuroAppointmentsQueryOptions(date?: string) {
  return queryOptions({
    queryKey: dashboardsQueryKeys.enrollmentNeuroAppointments(date),
    queryFn: () => fetchEnrollmentNeuroAppointments(date),
  });
}

export async function fetchEnrollmentEsOps(date?: string): Promise<EnrollmentEsOpsResponse> {
  const search = new URLSearchParams();
  if (date) search.set("date", date);
  const suffix = search.size > 0 ? `?${search}` : "";
  const body = await fetchEnrollmentJson(
    `/api/v1/dashboards/enrollment/es-ops${suffix}`,
    "Failed to load Enrollment ES ops",
  );
  if (!isEnrollmentEsOpsResponse(body)) {
    throw new Error("Enrollment ES ops response was malformed");
  }
  return body;
}

export function enrollmentEsOpsQueryOptions(date?: string) {
  return queryOptions({
    queryKey: dashboardsQueryKeys.enrollmentEsOps(date),
    queryFn: () => fetchEnrollmentEsOps(date),
  });
}

export async function fetchEnrollmentEsOpsCalls(
  date?: string,
): Promise<EnrollmentEsOpsCallsResponse> {
  const search = new URLSearchParams();
  if (date) search.set("date", date);
  const suffix = search.size > 0 ? `?${search}` : "";
  const body = await fetchEnrollmentJson(
    `/api/v1/dashboards/enrollment/es-ops-calls${suffix}`,
    "Failed to load Enrollment ES ops calls",
  );
  if (!isEnrollmentEsOpsCallsResponse(body)) {
    throw new Error("Enrollment ES ops calls response was malformed");
  }
  return body;
}

export function enrollmentEsOpsCallsQueryOptions(date?: string) {
  return queryOptions({
    queryKey: dashboardsQueryKeys.enrollmentEsOpsCalls(date),
    queryFn: () => fetchEnrollmentEsOpsCalls(date),
  });
}

export async function fetchEnrollmentEsConversion(
  date?: string,
): Promise<EnrollmentEsConversionResponse> {
  const search = new URLSearchParams();
  if (date) search.set("date", date);
  const suffix = search.size > 0 ? `?${search}` : "";
  const body = await fetchEnrollmentJson(
    `/api/v1/dashboards/enrollment/es-conversion${suffix}`,
    "Failed to load Enrollment ES conversion",
  );
  if (!isEnrollmentEsConversionResponse(body)) {
    throw new Error("Enrollment ES conversion response was malformed");
  }
  return body;
}

export function enrollmentEsConversionQueryOptions(date?: string) {
  return queryOptions({
    queryKey: dashboardsQueryKeys.enrollmentEsConversion(date),
    queryFn: () => fetchEnrollmentEsConversion(date),
  });
}

export async function fetchEnrollmentTrend(
  params: EnrollmentTrendParams = {},
): Promise<EnrollmentTrendResponse> {
  const search = new URLSearchParams({ window: params.window ?? "monthly" });
  if (params.start) search.set("start", params.start);
  if (params.end) search.set("end", params.end);
  const body = await fetchEnrollmentJson(
    `/api/v1/dashboards/enrollment/trend?${search}`,
    "Failed to load Enrollment trend",
  );
  if (!isEnrollmentTrendResponse(body)) {
    throw new Error("Enrollment trend response was malformed");
  }
  return body;
}

export function enrollmentTrendQueryOptions(params: EnrollmentTrendParams = {}) {
  return queryOptions({
    queryKey: dashboardsQueryKeys.enrollmentTrend(params),
    queryFn: () => fetchEnrollmentTrend(params),
  });
}

export async function fetchCareNavigationJson(
  path: string,
  init: RequestInit | undefined,
  fallbackMessage: string,
): Promise<unknown> {
  const response = await fetch(`${getOperatorsHttpOrigin()}${path}`, {
    credentials: "include",
    ...init,
  });
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have access to Dashboards.");
  if (!response.ok) {
    throw new Error(await readOperatorsAuthErrorMessage(response).catch(() => fallbackMessage));
  }
  return response.json();
}

export function careNavigationRosterQueryOptions() {
  return queryOptions({
    queryKey: [...dashboardsQueryKeys.all, "care-navigation", "roster"] as const,
    queryFn: async () => {
      const body = await fetchCareNavigationJson(
        "/api/v1/dashboards/care-navigation/roster",
        undefined,
        "Failed to load Care Navigation roster",
      );
      if (!Schema.is(CareNavigationRosterResponse)(body)) {
        throw new Error("Care Navigation roster response was malformed");
      }
      return body;
    },
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
  });
}

export async function fetchCareNavigationSnapshot(
  params: CareNavigationTimeframe,
): Promise<CareNavigationSnapshotResponse> {
  const search = new URLSearchParams({
    window: params.windowPreset,
    anchorDate: params.anchorDate,
    dateStart: params.dateStart,
    dateEnd: params.dateEnd,
    timezone: params.timezone,
    summaryOnly: "true",
  });
  const body = await fetchCareNavigationJson(
    `/api/v1/dashboards/care-navigation/snapshot?${search}`,
    undefined,
    "Failed to load Care Navigation snapshot",
  );
  if (!isCareNavigationSnapshotResponse(body)) {
    throw new Error("Care Navigation snapshot response was malformed");
  }
  return body;
}
export async function fetchCareNavigationDefinition(
  params: CareNavigationDefinitionRequest,
): Promise<CareNavigationDefinitionResponse> {
  const body = await fetchCareNavigationJson(
    "/api/v1/dashboards/care-navigation/definition",
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(params),
    },
    "Failed to load Care Navigation definition",
  );
  if (
    !body ||
    typeof body !== "object" ||
    !Array.isArray((body as CareNavigationDefinitionResponse).rows) ||
    typeof (body as CareNavigationDefinitionResponse).dateStart !== "string"
  ) {
    throw new Error("Care Navigation definition response was malformed");
  }
  return body as CareNavigationDefinitionResponse;
}

export function careNavigationDefinitionQueryOptions(params: CareNavigationDefinitionRequest) {
  return queryOptions({
    queryKey: [...dashboardsQueryKeys.all, "care-navigation", "definition", params] as const,
    queryFn: () => fetchCareNavigationDefinition(params),
  });
}

function isMonthlyCompletionResponse(
  value: unknown,
): value is CareNavigationMonthlyCompletionResponse {
  if (
    !value ||
    typeof value !== "object" ||
    !("rows" in value) ||
    !Array.isArray(value.rows) ||
    !("dateStart" in value) ||
    typeof value.dateStart !== "string" ||
    !("dateEnd" in value) ||
    typeof value.dateEnd !== "string"
  )
    return false;
  return value.rows.every(
    (row: unknown) =>
      row !== null &&
      typeof row === "object" &&
      "monthStart" in row &&
      typeof row.monthStart === "string" &&
      "navigatorUserId" in row &&
      typeof row.navigatorUserId === "string" &&
      "navigatorName" in row &&
      typeof row.navigatorName === "string" &&
      "alignedCount" in row &&
      typeof row.alignedCount === "number" &&
      Number.isFinite(row.alignedCount) &&
      "completedCount" in row &&
      typeof row.completedCount === "number" &&
      Number.isFinite(row.completedCount),
  );
}

export async function fetchCareNavigationMonthlyCompletion(
  anchorDate: string,
): Promise<CareNavigationMonthlyCompletionResponse> {
  const search = new URLSearchParams({ anchorDate });
  const body = await fetchCareNavigationJson(
    `/api/v1/dashboards/care-navigation/monthly-completion?${search}`,
    undefined,
    "Failed to load monthly completion",
  );
  if (!isMonthlyCompletionResponse(body))
    throw new Error("Monthly completion response was malformed");
  return body;
}

export function careNavigationMonthlyCompletionQueryOptions(anchorDate: string) {
  return queryOptions({
    queryKey: [
      ...dashboardsQueryKeys.all,
      "care-navigation",
      "monthly-completion",
      anchorDate,
    ] as const,
    queryFn: () => fetchCareNavigationMonthlyCompletion(anchorDate),
    staleTime: 5 * 60_000,
  });
}

export function careNavigationSnapshotQueryOptions(params: CareNavigationTimeframe) {
  return queryOptions({
    queryKey: dashboardsQueryKeys.careNavigationSnapshot(params),
    queryFn: () => fetchCareNavigationSnapshot(params),
  });
}

export async function fetchCareNavigationDrilldown(
  params: CareNavigationDrilldownRequest,
): Promise<CareNavigationDrilldownResponse> {
  const body = await fetchCareNavigationJson(
    "/api/v1/dashboards/care-navigation/drilldown",
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(params),
    },
    "Failed to load Care Navigation drilldown",
  );
  if (!isCareNavigationDrilldownResponse(body)) {
    throw new Error("Care Navigation drilldown response was malformed");
  }
  return body;
}

export function careNavigationDrilldownQueryOptions(params: CareNavigationDrilldownRequest) {
  return queryOptions({
    queryKey: dashboardsQueryKeys.careNavigationDrilldown(params),
    queryFn: () => fetchCareNavigationDrilldown(params),
  });
}

export async function fetchCareNavigationCalendarStatus(
  params: CareNavigationCalendarStatusRequest,
): Promise<CareNavigationCalendarStatusResponse> {
  const body = await fetchCareNavigationJson(
    "/api/v1/dashboards/care-navigation/calendar-status",
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(params),
    },
    "Failed to load Care Navigation calendar status",
  );
  if (!isCareNavigationCalendarStatusResponse(body)) {
    throw new Error("Care Navigation calendar status response was malformed");
  }
  return body;
}

export function careNavigationCalendarStatusQueryOptions(
  params: CareNavigationCalendarStatusRequest,
) {
  return queryOptions({
    queryKey: dashboardsQueryKeys.careNavigationCalendarStatus(params),
    queryFn: () => fetchCareNavigationCalendarStatus(params),
  });
}

// Phase 3 domain-local React Query modules — re-exported from the hub for
// discoverability. Domain dashboards may keep importing the local modules.
export {
  calKillerQueryKeys,
  calKillerSnapshotQueryOptions,
  fetchCalKillerSnapshot,
} from "./calKillerReactQuery";
export {
  buildDataDictionaryErdFrameUrl,
  dataDictionaryQueryKeys,
  dataDictionarySchemaQueryOptions,
  fetchDataDictionarySchema,
} from "./dataDictionaryReactQuery";
export {
  dcmpBillingQueryKeys,
  dcmpBillingSnapshotQueryOptions,
  fetchDcmpBillingSnapshot,
} from "./dcmpBillingReactQuery";
export {
  fetchInboundCallsSnapshot,
  inboundCallsQueryKeys,
  inboundCallsSnapshotQueryOptions,
} from "./inboundCallsReactQuery";
export {
  fetchReengagementEmailReport,
  fetchReengagementWaveEmails,
  reengagementEmailReportQueryOptions,
  reengagementEmailsQueryKeys,
} from "./reengagementEmailsReactQuery";
export {
  fetchRetellCallsCareSpaceBreakdown,
  fetchRetellCallsSummary,
  fetchRetellCallsSnapshot,
  fetchRetellCallsTransferReport,
  retellCallsCareSpaceBreakdownQueryOptions,
  retellCallsQueryKeys,
  retellCallsSnapshotQueryOptions,
  retellCallsSummaryQueryOptions,
  retellCallsTransferReportQueryOptions,
} from "./retellCallsReactQuery";
export {
  fetchRespiteNeedsAttention,
  fetchRespiteSummary,
  respiteNeedsAttentionQueryOptions,
  respiteQueryKeys,
  respiteSummaryQueryOptions,
} from "./respiteReactQuery";
export {
  fetchRevenueSnapshot,
  revenueQueryKeys,
  revenueSnapshotQueryOptions,
} from "./revenueReactQuery";
export {
  fetchWebsiteTrafficSnapshot,
  websiteTrafficQueryKeys,
  websiteTrafficSnapshotQueryOptions,
} from "./websiteTrafficQueries";
